import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { concatMap, EMPTY, first, map, Observable, of, withLatestFrom } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { CanvasElementFrom, FormContainerVariantType } from '@blaze-runtime/engine-domain';
import {
  DataBindingSourceInfo,
  DataBindingSourceType,
  getActiveBinding,
  PregenerationType,
} from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { BlazeSchema } from '@blaze-shared/schema-domain';
import {
  findFirstNodeInWorkflowOfType,
  FormActionType,
  isGetNode,
} from '@blaze-shared/workflow-domain';

import { RuntimeFacade } from '../../../+state';
import {
  filterOutInternalColumns,
  getColumnsFromSchema,
} from '../../../canvas-elements/element-model/data-binding';
import { hasDataProps } from '../../../canvas-elements/element-model/element-props-guards';
import { pregenerationTypeToAction } from '../../../utils/elements/generation.utils';

import { FormGenerationData } from './form-generation-data';

/**
 * Describes how the model of the form should be updated
 * `fullSchema` - uses full schema of the data source
 * `primaryTable` - use the primary dataset (e.g. insert/update scenarios)
 * `keep` - update the form without updating the schema and data source
 */
type FormGenerationSchemaType = 'fullSchema' | 'primaryTable' | 'keep';

/**
 * Resolves which data source a form is generated from and makes sure every schema the
 * generation will read is loaded first, so the generation itself can run synchronously.
 */
@Injectable({ providedIn: 'root' })
export class FormGenerationSourceService {
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);

  /**
   * Prepares and preloads schemas and returns the FormGenerationData object.
   *
   * Use this step to prepare the data for the `FormGenerationService.regenerateFormMutably`
   * method.
   *
   * @param formElement if form exists already, its properties will be used to discover the settings
   * @param generationData a partial FormGenerationData object used to provide initial/or forced values when generating/regenerating
   * @param elementDataSource the data source which is the base for pregeneration mechanism (either workflow or dataset)
   * - based on the selected action type the full schema or primary dataset will be used as the data source used for the form generation
   * @returns an Observable which emits the form generation data once all needed schemas are ready
   */
  prepareFormGenerationDataAndPreloadSchemas(
    formElement?: CanvasElementFrom<FormContainerVariantType>,
    generationData?: Partial<FormGenerationData>,
    elementDataSource?: DataBindingSourceInfo,
  ): Observable<FormGenerationData> {
    const pregenerationType =
      generationData?.pregenerationType || formElement?.props.content.pregenerationType;
    const action = getFormAction(formElement, pregenerationType);

    const elementValueDataSource = this.resolveValueDataSource(formElement, elementDataSource);
    if (!elementValueDataSource) {
      return EMPTY;
    }

    return this.prepareFormDataSource(
      elementValueDataSource,
      getSchemaType(generationData, action),
    ).pipe(
      withLatestFrom(
        this.schemaService
          .obtainAccountsSchema()
          .pipe(map((accountsDataset) => accountsDataset?.name)),
      ),
      map(([inputDataSource, accountsTableName]) => ({
        ...generationData,
        formId: formElement?.id,
        pregenerationType,
        inputDataSource,
        accountsTableName,
      })),
    );
  }

  /**
   * The data source the form's value is bound to. A mirror binding points at another
   * element, whose own value source is the one the form is generated from.
   */
  private resolveValueDataSource(
    formElement: CanvasElementFrom<FormContainerVariantType> | undefined,
    elementDataSource: DataBindingSourceInfo | undefined,
  ): DataBindingSourceInfo | undefined {
    const dataSource =
      elementDataSource || getActiveBinding(formElement?.props?.data, 'value')?.dataSource;
    if (!dataSource) {
      console.error('Element data source missing');
      return undefined;
    }
    if (dataSource.sourceType !== 'mirror') {
      return dataSource;
    }

    const targetElement = this.runtimeFacade.elementsValue[dataSource.id];
    if (!targetElement || !hasDataProps(targetElement)) {
      throw new Error('target element missing or invalid');
    }
    const targetDataSource = getActiveBinding(targetElement.props.data, 'value')?.dataSource;
    if (!targetDataSource) {
      throw new Error('target element datasource missing or invalid');
    }
    return targetDataSource;
  }

  /**
   * Prepares the form schema (obtains the latest known schemas) and also obtains all nested link schemas
   * @param elementValueDataSource the data source info used to obtain schemas
   * @param schemaType prepares the schema based on the full schema or the primary dataset
   * @returns Observable emitting the form schema
   */
  private prepareFormDataSource(
    elementValueDataSource: DataBindingSourceInfo,
    schemaType: FormGenerationSchemaType,
  ): Observable<DataBindingSourceInfo> {
    // If the data source should not be updated/refreshed - keep the previous one
    if (schemaType === 'keep') {
      return of(elementValueDataSource);
    }

    const isWorkflow = elementValueDataSource.sourceType === 'context';
    const targetSourceType: DataBindingSourceType =
      schemaType === 'fullSchema' ? elementValueDataSource.sourceType : 'dataset';
    const formSchema$ =
      schemaType === 'fullSchema'
        ? this.obtainFullSchema(elementValueDataSource, isWorkflow)
        : this.obtainPrimaryTableSchema(elementValueDataSource, isWorkflow);

    // Obtain nested schemas (link columns); emit the form schema as the result
    return formSchema$.pipe(
      // Ensure schemas for linked columns are fetched and ready (this is needed inside of the generation mechanism)
      concatMap((formSchema) => {
        if (!formSchema) {
          if (targetSourceType === 'api-call' && elementValueDataSource.id === 'NEW_API_CALL') {
            return EMPTY;
          }
          throw new Error('Could not fetch form schema');
        }

        // After obtaining all nested schemas -> emit the formSchema
        return this.schemaService.obtainNestedSchemas(formSchema).pipe(
          map(() => ({
            ...elementValueDataSource,
            sourceType: targetSourceType,
            columns: filterOutInternalColumns(getColumnsFromSchema(formSchema)),
            // Provide original workflow id if workflow is used, otherwise pass the resolved dataset id
            id: targetSourceType === 'context' ? elementValueDataSource.id : formSchema.id,
          })),
        );
      }),
    );
  }

  /** The whole schema behind the data source — the case for view/custom/prompt forms. */
  private obtainFullSchema(
    elementValueDataSource: DataBindingSourceInfo,
    isWorkflow: boolean,
  ): Observable<BlazeSchema | undefined> {
    if (elementValueDataSource.sourceType !== 'api-call') {
      return this.schemaService.obtainSchema(elementValueDataSource.id, isWorkflow);
    }
    if (elementValueDataSource.id !== 'NEW_API_CALL' && elementValueDataSource.name) {
      return of(
        this.schemaService.getApiCallSchema({
          connectorId: elementValueDataSource.id,
          actionName: elementValueDataSource.name,
        }),
      );
    }
    return of(undefined);
  }

  /**
   * The schema of the dataset the form writes into — the case for insert/update forms. For a
   * workflow source that is the dataset its first get node reads.
   */
  private obtainPrimaryTableSchema(
    elementValueDataSource: DataBindingSourceInfo,
    isWorkflow: boolean,
  ): Observable<BlazeSchema | undefined> {
    if (!isWorkflow) {
      return this.schemaService.obtainSchema(elementValueDataSource.id);
    }

    return this.store.select(runtimeSelectors.selectWorkflowById(elementValueDataSource.id)).pipe(
      first(),
      map((workflow) => {
        if (!workflow) {
          throw new Error(`Workflow ${elementValueDataSource.id} missing`);
        }
        const firstGetNode = findFirstNodeInWorkflowOfType(workflow, isGetNode);
        if (!firstGetNode || !firstGetNode.datasetId) {
          throw new Error(`Get node or dataset missing in workflow ${elementValueDataSource.id}`);
        }
        return firstGetNode.datasetId;
      }),
      concatMap((primaryDatasetId) => this.schemaService.obtainSchema(primaryDatasetId)),
    );
  }
}

/** The action stored on the form, or the one its pregeneration type implies. */
function getFormAction(
  formElement: CanvasElementFrom<FormContainerVariantType> | undefined,
  pregenerationType: PregenerationType | undefined,
): FormActionType | undefined {
  const action = formElement?.props?.content?.action;
  if (!action && pregenerationType) {
    return pregenerationTypeToAction(pregenerationType);
  }
  return action;
}

/** Which schema the form's model is refreshed from, given what it is being generated for. */
function getSchemaType(
  generationData: Partial<FormGenerationData> | undefined,
  action: FormActionType | undefined,
): FormGenerationSchemaType {
  if (generationData?.keepSchema === true) {
    return 'keep';
  }

  switch (action) {
    case 'view':
    case 'custom':
    case 'prompt':
      return 'fullSchema';
    case 'insert':
    case 'update':
      return 'primaryTable';
    default:
      throw new Error(`Action '${action}' not supported`);
  }
}
